import React from "react"
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material"
import { OrderStatus } from "@/types/enums/OrderStatus"
import { getOrderWorkflowViewModel } from "@/domain/order/order-details-stepper"
import { WorkflowStepperTrackRow } from "@/components/order/order-workflow-stepper-shared"

export type OrderWorkflowStepperProps = {
  orderStatus: OrderStatus
  /** Pickup order (persisted order type): Pickup step labels instead of the Delivery ones. */
  isPickup?: boolean
}

function WorkflowScreenReaderLive({ summary }: { summary: string }) {
  const theme = useTheme()
  return (
    <Box
      component="span"
      sx={{
        position: "absolute",
        width: theme.spacing(0.25),
        height: theme.spacing(0.25),
        p: 0,
        m: theme.spacing(-0.25),
        overflow: "hidden",
        clipPath: "inset(50%)",
        whiteSpace: "nowrap",
        border: 0,
      }}
      aria-live="polite"
      aria-atomic="true"
    >
      {summary}
    </Box>
  )
}

function OrderWorkflowStepperDesktop({ orderStatus, isPickup }: OrderWorkflowStepperProps) {
  const theme = useTheme()
  const viewModel = getOrderWorkflowViewModel(orderStatus, { isPickup })
  const { progress } = viewModel

  return (
    <Box
      component="nav"
      aria-label="Order progress"
      sx={{ width: "100%", overflow: "hidden" }}
    >
      <WorkflowScreenReaderLive summary={viewModel.screenReaderSummary} />
      <WorkflowStepperTrackRow
        steps={progress.steps}
        progress={progress}
        showLabel
        layoutKey="desktop"
      />
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: theme.spacing(0.75),
          color: theme.palette.text.secondary,
          fontWeight: theme.typography.fontWeightMedium,
        }}
      >
        {viewModel.progressLabel}
      </Typography>
    </Box>
  )
}

function OrderWorkflowStepperMobile({ orderStatus, isPickup }: OrderWorkflowStepperProps) {
  const theme = useTheme()
  const viewModel = getOrderWorkflowViewModel(orderStatus, { isPickup })

  return (
    <Box
      component="nav"
      aria-label="Order progress"
      sx={{ width: "100%", overflow: "hidden" }}
    >
      <WorkflowScreenReaderLive summary={viewModel.screenReaderSummary} />
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mb: theme.spacing(0.75),
          color: theme.palette.text.secondary,
          fontWeight: theme.typography.fontWeightBold,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        Order Progress
      </Typography>
      <WorkflowStepperTrackRow
        steps={viewModel.progress.steps}
        progress={viewModel.progress}
        compact
        layoutKey="mobile"
      />
      {/* Textual context only — the current-stage icon lives in the track row above. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mt: theme.spacing(1),
          minWidth: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: theme.typography.fontWeightBold,
              color: theme.palette.text.primary,
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {viewModel.currentStepLabel}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
            {viewModel.progressLabel}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

/**
 * Premium order lifecycle stepper with responsive desktop/mobile layouts.
 * Payment status is intentionally excluded — use badges in the parent header.
 * Render only when {@link getOrderWorkflowPresentation} returns `mode: "workflow"`.
 */
export function OrderWorkflowStepper({ orderStatus, isPickup }: OrderWorkflowStepperProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  if (isMobile) {
    return <OrderWorkflowStepperMobile orderStatus={orderStatus} isPickup={isPickup} />
  }

  return <OrderWorkflowStepperDesktop orderStatus={orderStatus} isPickup={isPickup} />
}
